/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SIMON_LEVELS, SIMON_THEMES, playButtonTone, playWrongTone, playSuccessFanfare, speakAnimalSound } from "@/utils/simonGameData";
import { Home, Star, Volume2, VolumeX, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";
import axios from "axios";

import { logger } from "@/utils/logger";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SimonGame = ({ startLevel = null, onBack }) => {
  const [phase, setPhase] = useState("ready"); // ready, showing, input, roundWin, levelUp, gameover, allDone
  const [levels, setLevels] = useState([]);
  const [levelIdx, setLevelIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [stars, setStars] = useState(0);
  const [lives, setLives] = useState(3);
  const [soundOn, setSoundOn] = useState(true);

  const [sequence, setSequence] = useState([]);
  const [playerIdx, setPlayerIdx] = useState(0);
  const [activeBtn, setActiveBtn] = useState(null);
  const [wrongBtn, setWrongBtn] = useState(null);
  const [roundNum, setRoundNum] = useState(0);
  const [miniConfetti, setMiniConfetti] = useState(false);

  const [saveName, setSaveName] = useState(() => {
    try { const d = localStorage.getItem('alba_game_players'); if (d) { const p = JSON.parse(d); return p[0] || ""; } } catch {} return "";
  });
  const [scoreSaved, setScoreSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const showingRef = useRef(false);
  const { width, height } = useWindowSize();

  useEffect(() => {
    if (startLevel !== null) {
      const lvl = SIMON_LEVELS.find(l => l.id === startLevel);
      setLevels(lvl ? [lvl] : [...SIMON_LEVELS]);
    } else {
      setLevels([...SIMON_LEVELS]);
    }
  }, [startLevel]);

  const currentLevel = levels[levelIdx];
  const theme = currentLevel ? SIMON_THEMES[currentLevel.theme] : null;
  const buttons = theme ? theme.items.slice(0, currentLevel.buttonCount) : [];

  // Generate new sequence for current round
  const generateSequence = useCallback(() => {
    if (!currentLevel) return;
    const len = Math.min(currentLevel.startLen + roundNum, currentLevel.maxLen);
    const seq = [];
    for (let i = 0; i < len; i++) {
      seq.push(Math.floor(Math.random() * currentLevel.buttonCount));
    }
    setSequence(seq);
    setPlayerIdx(0);
  }, [currentLevel, roundNum]);

  // Show the sequence to the player
  const showSequence = useCallback(async () => {
    if (!currentLevel || showingRef.current) return;
    showingRef.current = true;
    setPhase("showing");

    const speed = currentLevel.showSpeed;
    const seq = sequence.length > 0 ? sequence : (() => {
      const len = Math.min(currentLevel.startLen + roundNum, currentLevel.maxLen);
      const s = [];
      for (let i = 0; i < len; i++) s.push(Math.floor(Math.random() * currentLevel.buttonCount));
      setSequence(s);
      return s;
    })();

    for (let i = 0; i < seq.length; i++) {
      await new Promise(r => setTimeout(r, 300));
      setActiveBtn(seq[i]);
      if (soundOn) {
        playButtonTone(seq[i]);
        const btn = buttons[seq[i]];
        if (btn?.sound) speakAnimalSound(btn.sound);
      }
      await new Promise(r => setTimeout(r, speed));
      setActiveBtn(null);
    }

    await new Promise(r => setTimeout(r, 200));
    showingRef.current = false;
    setPhase("input");
  }, [currentLevel, sequence, roundNum, soundOn, buttons]);

  // Start round
  useEffect(() => {
    if (phase === "ready" && currentLevel) {
      generateSequence();
    }
  }, [phase, currentLevel, generateSequence]);

  useEffect(() => {
    if (phase === "ready" && sequence.length > 0) {
      const t = setTimeout(() => showSequence(), 600);
      return () => clearTimeout(t);
    }
  }, [sequence, phase]);

  // Handle player click
  const handleButtonClick = (btnIndex) => {
    if (phase !== "input" || showingRef.current) return;

    setActiveBtn(btnIndex);
    if (soundOn) {
      playButtonTone(btnIndex);
      const btn = buttons[btnIndex];
      if (btn?.sound) speakAnimalSound(btn.sound);
    }
    setTimeout(() => setActiveBtn(null), 250);

    if (btnIndex === sequence[playerIdx]) {
      // Correct!
      const nextIdx = playerIdx + 1;
      setPlayerIdx(nextIdx);

      if (nextIdx >= sequence.length) {
        // Round complete!
        setScore(prev => prev + sequence.length * 5);
        setStars(prev => prev + 1);
        setMiniConfetti(true);
        setTimeout(() => setMiniConfetti(false), 2000);

        if (roundNum + 1 >= 3 || sequence.length >= currentLevel.maxLen) {
          // Level complete
          if (soundOn) playSuccessFanfare();
          setTimeout(() => {
            if (levelIdx + 1 < levels.length) {
              setPhase("levelUp");
            } else {
              setPhase("allDone");
              recordGameWon("simon");
            }
          }, 1000);
        } else {
          // Next round
          setPhase("roundWin");
          if (soundOn) playSuccessFanfare();
          setTimeout(() => {
            setRoundNum(prev => prev + 1);
            setPlayerIdx(0);
            setPhase("ready");
          }, 1500);
        }
      }
    } else {
      // Wrong - just replay the sequence
      setWrongBtn(btnIndex);
      if (soundOn) playWrongTone();
      setTimeout(() => setWrongBtn(null), 500);

      // Always replay - no life lost
      setTimeout(() => {
        setPlayerIdx(0);
        showSequence();
      }, 1000);
    }
  };

  // Next level
  const goNextLevel = () => {
    setLevelIdx(prev => prev + 1);
    setRoundNum(0);
    setLives(levels[levelIdx + 1]?.lives || 3);
    setSequence([]);
    setPlayerIdx(0);
    setPhase("ready");
  };

  // Restart
  const restartGame = () => {
    setLevelIdx(0); setRoundNum(0); setScore(0); setStars(0);
    setLives(levels[0]?.lives || 3); setSequence([]); setPlayerIdx(0);
    setSaveName(""); setScoreSaved(false); setPhase("ready");
  };

  // Save
  const handleSaveScore = async () => {
    if (!saveName.trim() || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/game/record`, {
        player_name: saveName.trim(), mode: "simon", duration_seconds: 0,
        score, stars, level: currentLevel?.name || "All", opponent_type: "solo",
      });
      setScoreSaved(true);
    } catch (e) { logger.error(e); }
    setSaving(false);
  };

  if (!currentLevel) return null;

  const lvlColor = currentLevel.color;
  const isGrid6 = buttons.length > 4;

  return (
    <div className="flex flex-col items-center w-full max-w-5xl mx-auto px-2" data-testid="simon-game-container">
      {(phase === "allDone" || miniConfetti) && (
        <Confetti width={width} height={height} colors={buttons.map(b => b.color)}
          recycle={false} numberOfPieces={phase === "allDone" ? 400 : 80} gravity={0.3} />
      )}

      {/* Top Bar */}
      {phase !== "allDone" && phase !== "gameover" && phase !== "levelUp" && (
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full mb-3">
          <div className="glass-card-strong rounded-xl p-3 border border-gold/20">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg">{currentLevel.icon}</span>
                <span className="font-fredoka text-sm" style={{ color: lvlColor }}>
                  Lv{currentLevel.id} {currentLevel.name}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-white fill-gold" />
                  <span className="font-fredoka text-white text-sm" data-testid="simon-score">{score}</span>
                </div>
                <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm"
                  className="border-gold/20 text-gold hover:bg-gold/10 h-8 w-8 p-0" data-testid="simon-sound-toggle">
                  {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                </Button>
                <Button onClick={onBack} variant="outline" size="sm"
                  className="border-gold/20 text-pink-300 hover:bg-gold/10 h-8 px-2" data-testid="simon-back-btn">
                  <Home className="w-3.5 h-3.5 mr-1" />Menu
                </Button>
              </div>
            </div>

            {/* Status */}
            <div className="rounded-lg p-2 text-center border" style={{ borderColor: `${lvlColor}33`, background: `${lvlColor}08` }}>
              <p className="font-fredoka text-sm font-bold" style={{ color: lvlColor }} data-testid="simon-status">
                {phase === "showing" && "Perhatikan urutannya..."}
                {phase === "input" && `Giliranmu! (${playerIdx}/${sequence.length})`}
                {phase === "roundWin" && "Bagus! Bersiap ronde baru..."}
                {phase === "ready" && "Bersiap..."}
              </p>
              {/* Sequence progress dots */}
              {phase === "input" && (
                <div className="flex gap-1 justify-center mt-1">
                  {sequence.map((_, i) => (
                    <div key={i} className="w-2.5 h-2.5 rounded-full transition-all"
                      style={{ background: i < playerIdx ? '#4ade80' : i === playerIdx ? '#FFD700' : '#3a2a1a' }} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Game Buttons */}
      {phase !== "allDone" && phase !== "gameover" && phase !== "levelUp" && (
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className={`grid gap-4 ${isGrid6 ? 'grid-cols-3' : 'grid-cols-2'} max-w-xl mx-auto`}>
          {buttons.map((btn, idx) => {
            const isActive = activeBtn === idx;
            const isWrong = wrongBtn === idx;
            const isDisabled = phase !== "input";
            return (
              <motion.button
                key={btn.id}
                data-testid={`simon-btn-${idx}`}
                onClick={() => handleButtonClick(idx)}
                disabled={isDisabled}
                whileHover={!isDisabled ? { scale: 1.05 } : {}}
                whileTap={!isDisabled ? { scale: 0.92 } : {}}
                animate={isActive ? { scale: [1, 1.12, 1] } : isWrong ? { x: [-4, 4, -4, 4, 0] } : {}}
                transition={{ duration: isActive ? 0.25 : 0.3 }}
                className="rounded-2xl flex flex-col items-center justify-center gap-1 border-4 transition-all"
                style={{
                  width: isGrid6 ? 140 : 175,
                  height: isGrid6 ? 140 : 175,
                  background: isActive ? btn.activeColor : isWrong ? '#EF444480' : `${btn.color}30`,
                  borderColor: isActive ? btn.activeColor : isWrong ? '#EF4444' : btn.color,
                  boxShadow: isActive
                    ? `0 0 30px ${btn.activeColor}, 0 0 60px ${btn.color}40, inset 0 0 20px ${btn.activeColor}40`
                    : isWrong ? '0 0 20px #EF444440' : `0 4px 15px ${btn.color}20`,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled && !isActive ? 0.7 : 1,
                }}>
                {btn.icon ? (
                  <span className="text-4xl" style={{ filter: isActive ? 'brightness(1.4)' : 'none' }}>{btn.icon}</span>
                ) : (
                  <div className="w-14 h-14 rounded-full" style={{ background: isActive ? '#fff' : btn.color, boxShadow: isActive ? `0 0 15px ${btn.color}` : 'none' }} />
                )}
                <span className="font-fredoka text-sm font-bold" style={{ color: isActive ? '#1a1a1a' : btn.color }}>
                  {btn.label}
                </span>
              </motion.button>
            );
          })}
        </motion.div>
      )}

      {/* Round info */}
      {phase !== "allDone" && phase !== "gameover" && phase !== "levelUp" && (
        <div className="mt-3 text-center">
          <p className="font-fredoka text-gold-light/80/60 text-xs">
            Ronde {roundNum + 1} | Panjang urutan: {sequence.length}
          </p>
        </div>
      )}

      {/* Level Up Modal */}
      <AnimatePresence>
        {phase === "levelUp" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: "spring" }}
              className="rounded-2xl p-6 border-2 text-center max-w-md w-full"
              style={{ background: 'rgba(30,20,10,0.95)', borderColor: `${lvlColor}44` }}>
              <span className="text-4xl block mb-2">{currentLevel.icon}</span>
              <h2 className="font-fredoka text-xl sm:text-2xl font-bold mb-2" style={{ color: lvlColor }}>
                Level {currentLevel.id} Selesai!
              </h2>
              <p className="font-fredoka text-gold-light/80 text-sm mb-3">{currentLevel.name}</p>
              <div className="flex justify-center gap-1 mb-4">
                {[1,2,3].map(s => (
                  <Star key={s} className={`w-8 h-8 ${s <= Math.min(3, stars) ? 'fill-current' : ''}`}
                    style={{ color: s <= Math.min(3, stars) ? '#FFD700' : '#3a2a1a' }} />
                ))}
              </div>
              <p className="font-fredoka text-gold-light/80 text-sm mb-4">Skor: <span className="font-bold text-white">{score}</span></p>
              <Button onClick={goNextLevel} className="w-full font-fredoka font-bold text-brown-dark bg-gradient-to-r from-yellow-400 to-amber-500 hover:bg-gradient-to-r from-yellow-400 to-amber-500/90"
                data-testid="simon-next-level-btn">Lanjut Level Berikutnya!</Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Game Over */}
      <AnimatePresence>
        {phase === "gameover" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }}
              className="rounded-2xl p-6 border-2 text-center max-w-md w-full"
              style={{ background: 'rgba(30,20,10,0.95)', borderColor: '#FF444444' }}>
              <h2 className="font-fredoka text-2xl font-bold text-red-400 mb-2">Nyawa Habis!</h2>
              <p className="font-fredoka text-gold-light/80 text-sm mb-3">Jangan menyerah, coba lagi!</p>
              <p className="font-fredoka text-gold-light/80 text-sm mb-4">Skor: <span className="font-bold text-white">{score}</span></p>
              <div className="flex flex-col gap-2">
                <Button onClick={restartGame} className="w-full font-fredoka font-bold text-brown-dark bg-gradient-to-r from-yellow-400 to-amber-500 hover:bg-gradient-to-r from-yellow-400 to-amber-500/90"
                  data-testid="simon-retry-btn">Main Lagi</Button>
                <Button onClick={onBack} variant="outline" className="w-full border-gold/20 text-pink-300 hover:bg-gold/10 font-fredoka"
                  data-testid="simon-gameover-back-btn"><Home className="w-4 h-4 mr-2" />Kembali</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* All Done */}
      <AnimatePresence>
        {phase === "allDone" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", damping: 15 }}
              className="rounded-2xl p-6 sm:p-8 border-2 text-center max-w-md w-full"
              style={{ background: 'rgba(30,20,10,0.95)', borderColor: '#FFD70044', boxShadow: '0 0 60px rgba(255,215,0,0.2)' }}>
              <Trophy className="w-16 h-16 mx-auto mb-3 text-white" />
              <h2 className="font-fredoka text-2xl sm:text-3xl font-bold text-white mb-2">Semua Level Selesai!</h2>
              <p className="font-fredoka text-gold-light/80 mb-3">Ingatanmu luar biasa!</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-lg p-3 border border-gold/20/20 glass-card">
                  <p className="font-fredoka text-gold-light/80 text-xs">Skor</p>
                  <p className="font-fredoka text-2xl text-gold font-bold">{score}</p>
                </div>
                <div className="rounded-lg p-3 border border-gold/20/20 glass-card">
                  <p className="font-fredoka text-gold-light/80 text-xs">Bintang</p>
                  <p className="font-fredoka text-2xl text-gold font-bold">{stars}</p>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {!scoreSaved ? (
                  <div className="flex gap-2 mb-1">
                    <input data-testid="simon-save-name-input" type="text" value={saveName}
                      onChange={(e) => setSaveName(e.target.value)} placeholder="Nama kamu..." maxLength={50}
                      className="flex-1 glass-card border border-gold/20 rounded-lg px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-gold/20"
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveScore()} />
                    <Button onClick={handleSaveScore} disabled={!saveName.trim() || saving}
                      className="font-fredoka font-bold text-brown-dark px-4" style={{ background: '#4ade80' }}
                      data-testid="simon-save-score-btn">{saving ? '...' : 'Simpan'}</Button>
                  </div>
                ) : <p className="text-green-400 font-fredoka text-sm mb-1" data-testid="simon-score-saved">Skor tersimpan!</p>}
                <Button onClick={restartGame} className="w-full font-fredoka font-bold text-brown-dark bg-gradient-to-r from-yellow-400 to-amber-500 hover:bg-gradient-to-r from-yellow-400 to-amber-500/90"
                  data-testid="simon-replay-btn">Main Lagi</Button>
                <Button onClick={onBack} variant="outline" className="w-full border-gold/20 text-pink-300 hover:bg-gold/10 font-fredoka"
                  data-testid="simon-finish-btn"><Home className="w-4 h-4 mr-2" />Kembali ke Menu</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SimonGame;
